import { registerIndicator, type IndicatorDrawParams, type KLineData } from 'klinecharts'
import type { SplitEvent } from '../types/splits'
import { registerOnce } from './registrationUtils'
import { DEFAULT_SESSION_TZ } from './sessionDay'
import { CHART_FONT_FAMILY } from './chartColors'
import { earningsBarIndex, type EarningsDrawCtx } from './earningsMarkersIndicator'

/**
 * Split markers - a small outlined "S" circle pinned to the BOTTOM of the
 * price pane at each split execution day: transparent inside, 1px violet
 * border, violet letter. The ratio ("R/S 10:1") is NOT drawn inline - it
 * clutters the chart - it lives in the hover card (SplitTooltip), matching
 * the earnings-badge hover pattern. Structured exactly like the earnings
 * markers indicator (`earningsMarkersIndicator.ts`): a SYSTEM custom-draw
 * indicator excluded from the `s` hide-all toggle, with the splits array +
 * session timezone riding in via `extendData` so `draw` is a pure function
 * of the loaded bars and redraws on pan/zoom for free.
 *
 * One violet for all split kinds - deliberately outside the beat/miss
 * green/red vocabulary so a split badge is never read as a result.
 */

const INDICATOR_NAME = 'SPLIT_MARKERS'

export const SPLIT_BADGE_COLOR = '#8b5cf6'

export interface SplitMarkersConfig {
  splits: SplitEvent[]
  timezone: string
}

// splitLabel lives in a klinecharts-free leaf module so non-chart hosts can
// import it without pulling in the chart engine; re-exported here for the
// chart's own call sites and existing tests.
export { splitLabel } from './splitLabel'

/** Badge geometry - a circle matching the earnings badge's footprint, sitting
 *  just above the pane bottom. */
const BADGE_R = 7
const BADGE_BOTTOM_GAP = 4

export interface SplitDrawParams {
  ctx: EarningsDrawCtx
  kLineDataList: Array<{ timestamp: number } | undefined>
  visibleRange: { from: number; to: number }
  bounding: { height: number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  splits: SplitEvent[]
  timezone: string
}

/**
 * Paint one outlined "S" circle per in-range split event, at the x of the
 * event's session-day first bar. Pure over its params so it's unit-testable
 * with a fake ctx (mirrors `drawEarningsMarkers`). Events with no matching bar
 * in the loaded range (including future-dated upcoming splits) are skipped.
 */
export function drawSplitMarkers(params: SplitDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, bounding, xAxis, splits, timezone } = params
  if (!splits.length || kLineDataList.length === 0) return true

  const { from, to } = visibleRange
  const cy = bounding.height - BADGE_R - BADGE_BOTTOM_GAP

  ctx.save()
  ctx.font = `10px ${CHART_FONT_FAMILY}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // The 2D context is shared across indicator draws and may carry a dash
  // pattern from a previous one (grid / dashed lines) - the border must be solid.
  ctx.setLineDash([])

  for (const ev of splits) {
    const idx = earningsBarIndex(kLineDataList, ev.date, timezone)
    // Skip events with no matching bar, or bars outside the visible window.
    if (idx < 0 || idx < from || idx >= to) continue

    const cx = xAxis.convertToPixel(idx)

    // Outlined circle: TRANSPARENT inside - just the 1px border and the letter
    // in the split violet, so the badge sits over the chart without a plate.
    ctx.beginPath()
    ctx.arc(cx, cy, BADGE_R, 0, Math.PI * 2)
    ctx.closePath()
    ctx.strokeStyle = SPLIT_BADGE_COLOR
    ctx.lineWidth = 1
    ctx.stroke()

    ctx.fillStyle = SPLIT_BADGE_COLOR
    ctx.fillText('S', cx, cy)
  }

  ctx.restore()
  return true
}

/** Register the `SPLIT_MARKERS` main-pane indicator with klinecharts (once). */
export const registerSplitMarkersIndicator = registerOnce(() => {
  registerIndicator({
    name: INDICATOR_NAME,
    shortName: '',
    // No figures: this indicator carries no series values, it only custom-draws
    // badges. `calc` is a no-op carrier so klinecharts keeps the instance alive.
    figures: [],
    calc: (dataList: KLineData[]) => dataList.map(() => ({})),
    draw: (params: IndicatorDrawParams) => {
      const { ctx, kLineDataList, visibleRange, bounding, xAxis, indicator } = params
      const config = indicator.extendData as SplitMarkersConfig | undefined
      if (!config || !config.splits?.length) return false

      return drawSplitMarkers({
        ctx: ctx as unknown as EarningsDrawCtx,
        kLineDataList,
        visibleRange,
        bounding,
        xAxis,
        splits: config.splits,
        timezone: config.timezone || DEFAULT_SESSION_TZ,
      })
    },
  })
})

export const SPLIT_MARKERS_NAME = INDICATOR_NAME
